import { ScaleTime } from "@visx/vendor/d3-scale";
import { GenericTimelineData } from "gen/model/genericTimelineData";
import { GenericTimelineFlag } from "gen/model/genericTimelineFlag";
import { GenericTimelinePeriod } from "gen/model/genericTimelinePeriod";
import { GenericTimelineRow } from "gen/model/genericTimelineRow";
import { last, memoize } from "lodash-es";
import { useMemo } from "react";
import { TimelineConstants } from "shared/components/timeline/TimelineConstants";
import { measureText } from "shared/utils/MeasureText";
import { textStyles } from "theme/textStyles";

export interface FlagLayout {
  flag: GenericTimelineFlag;
  line: {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
  };
  bar: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  text: {
    x: number;
    y: number;
  };
}

export interface RowLayout {
  row: GenericTimelineRow;
  badge: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  periods: {
    period: GenericTimelinePeriod;
    bar: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  }[];
}

const {
  rowHeight,
  rowSpacing,
  xPadding,
  flagTextPaddingX,
  flagMinSpacing,
  flagHeight,
  barStrokeWidth,
  flagOffsetOffsetFromLine,
} = TimelineConstants;

export const useTimelineFlagsLayout = (
  data: GenericTimelineData,
  timelineWidth: number,
  xScale: ScaleTime<number, number>,
  yPadding: number,
) =>
  useMemo(() => {
    const flagRows = [];
    for (const flag of data.flags) {
      const x = xScale(new Date(flag.at));
      const width = getFlagWidth(flag);
      const isNearEnd = x + width > timelineWidth;
      const flagLayout: FlagLayout = {
        flag,
        bar: {
          x: isNearEnd
            ? x - width + flagOffsetOffsetFromLine
            : x - flagOffsetOffsetFromLine,
          y: 0,
          width,
          height: flagHeight,
        },
        line: {
          x1: x,
          x2: x,
          y1: flagHeight - 1, // -1px so line is under the bar
          y2:
            data.rows.length * (rowHeight + rowSpacing) -
            yPadding * 2 - // top and bottom
            barStrokeWidth / 2,
        },
        text: {
          x: isNearEnd
            ? x + flagOffsetOffsetFromLine - width / 2
            : x - flagOffsetOffsetFromLine + width / 2,
          y: flagHeight / 2,
        },
      };

      const flagRowIndex = flagRows.findIndex((row) => {
        const lastFlagLayout = last(row);
        if (!lastFlagLayout) {
          return true;
        }
        return (
          lastFlagLayout.bar.x + lastFlagLayout.bar.width + flagMinSpacing <
          flagLayout.bar.x
        );
      });

      const offsetY =
        (flagRowIndex === -1 ? flagRows.length : flagRowIndex) *
        (flagHeight + flagMinSpacing);
      flagLayout.bar.y += offsetY;
      flagLayout.text.y += offsetY;
      flagLayout.line.y1 += offsetY;

      if (flagRowIndex === -1) {
        flagRows.push([flagLayout]);
      } else {
        const flagRow = flagRows[flagRowIndex];
        flagRow.push(flagLayout);
      }
    }

    const startY = flagRows.length * (flagHeight + flagMinSpacing) + yPadding;
    for (const flagRow of flagRows) {
      for (const flagLayout of flagRow) {
        flagLayout.line.y2 += startY;
      }
    }

    return { flagRows, startY } as const;
  }, [data, timelineWidth, xScale, yPadding]);

export const useRowsLayout = (
  data: GenericTimelineData,
  startX: number,
  xScale: ScaleTime<number, number>,
) =>
  useMemo<RowLayout[]>(
    () =>
      data.rows.map((row, rowIndex) => ({
        row,
        badge: {
          x: 0,
          y: rowIndex * (rowHeight + rowSpacing),
          width: startX - xPadding - barStrokeWidth,
          height: rowHeight,
        },
        periods: row.periods.map((period) => {
          const x = xScale(new Date(period.start_at));
          const y = rowIndex * (rowHeight + rowSpacing);
          const width = xScale(new Date(period.end_at)) - x;
          return {
            period,
            bar: {
              x,
              y,
              width,
              height: rowHeight,
            },
          };
        }),
      })),
    [data.rows, startX, xScale],
  );

const memoizedMeasureText = memoize(measureText);

const getFlagWidth = (flag: GenericTimelineFlag) =>
  memoizedMeasureText(
    flag.label,
    "Inter",
    textStyles.small.fontSize as string,
    textStyles.small.fontWeight as string,
  ) +
  flagTextPaddingX * 2; // left and right padding
